<template>
    <div class="row">
        <div class="col">
            <sub-section title="Here is your plan to get what you want" :showHelp="false">
                <p>
                    When you retire, take the amounts shown in the table below, from your pension pots in each year.
                </p>

                <p>
                    This should give what you want in total when added to any savings withdrawals and everything else shown. 
                    If you want to see monthly amounts, click on monthly values.
                    <template v-if="displayTaxSavingInfo">
                        Following this plan could reduce your income tax paid by around <strong>{{ taxSavingAmount }}</strong>, by 
                        using your tax free allowance efficiently.
                        <Help title="Making your plan tax efficient" :text="HelpText.finishTaxEfficiency()"></Help>
                    </template>
                </p>

                <plan-table ref="planTable"></plan-table>

                <register ref="register" :retiree="retiree"></register>

                <template v-slot:footer>
                    <div v-if="canViewPlanLaterYears || canDownloadReport" class="card-footer d-grid gap-2 d-md-flex justify-content-md-between mt-4">
                        <button v-if="canViewPlanLaterYears" class="btn btn-outline-dark me-2 my-1" @click="onToggleDisplayLaterYears"><i class="bi bi-calendar me-2"></i> {{ toggleLaterYearsText }}</button>
                        <button v-if="canDownloadReport" class="btn btn-dark my-1" @click="onDownloadReport"><i class="bi bi-download me-2"></i> Download your report</button>
                    </div>
                </template>
            </sub-section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { Retiree } from "@/guiide/ts/retiree";
import { useStore } from "vuex";
import { app } from "@/guiide/ts/app";
import { formatter } from "@/db/ts/formatter";
import { FinishInfoResponse } from "@/guiide/ts/response/finishInfoResponse";
import HelpText from "@/guiide/ts/helpText";
import SubSection from "@/guiide/components/common/SubSection.vue";
import PlanTable from "../common/PlanTable.vue";
import Register from "./Register.vue";
import Help from "../common/Help.vue";

const props = defineProps({
    retiree: {
        type: Object as () => Retiree | null,
        default: () => null,
    },
    finishInfo: {
        type: Object as () => FinishInfoResponse,
        default: () => new FinishInfoResponse(false, 0, []),
    }
});

const store = useStore();
const planTable = ref(null);
const register = ref(null);
const showLaterYearsText = "Show later years";
const hideLaterYearsText = "Hide later years";
const toggleLaterYearsText = ref(showLaterYearsText);
const taxSavingAmount = ref("");
const displayTaxSavingInfo = ref(false);

watch(
    () => props.finishInfo,
    (newFinishInfo: FinishInfoResponse) => {
        if (newFinishInfo == null) {
            return;
        }

        displayTaxSavingInfo.value = false;
        taxSavingAmount.value = "";

        if (newFinishInfo.hasTaxLiability && newFinishInfo.planTaxSaving >= 100) {
            displayTaxSavingInfo.value = true;
            taxSavingAmount.value = formatter.formatCurrency(newFinishInfo.planTaxSaving);
        }
    },
    { immediate: true }
)

const canViewPlanLaterYears = computed<boolean>(() => {
    if (store.state.configuration.finishCanViewPlanWithoutSignup) {
        return true;
    }

    return store.state.retiree?.isRegistered ?? false;
});

const canDownloadReport = computed<boolean>(() => {
    return store.state.configuration.finishCanDownloadReport;
});

function onToggleDisplayLaterYears() {
    if (!canShowLaterYearsOrDownloadReport()) {
        register.value.alert();
        return;
    }

    planTable.value.toggleFullPlanDisplay();
    toggleLaterYearsText.value = toggleLaterYearsText.value == showLaterYearsText ? hideLaterYearsText : showLaterYearsText;
}

async function onDownloadReport() {
    if (!canShowLaterYearsOrDownloadReport()) {
        register.value.alert();
        return;
    }

    app.downloadReport();
}

function canShowLaterYearsOrDownloadReport(): boolean {
    const canViewPlanWithoutRegistration = store.state.configuration.finishCanViewPlanWithoutSignup;

    if (canViewPlanWithoutRegistration) {
        return true;
    }

    const isRegistered = store.state.retiree?.isRegistered ?? false;
    return isRegistered;
}
</script>

<style scoped>
    .card-footer {
        background-color: white;
        margin-top: 32px;
        padding: 0;
        padding-top: 32px;
        border-top: 1px solid #ececec;
    }
</style>
